// Display helpers for C-12 / S-04 Health / S-07: incident lines, the tooltip date, "since" times. Times are in the
// clinic's time zone (C-12, S-04) or the viewer's (S-07's cross-clinic history, API.md §1.4).
import { fmtDayOf, fmtTime, MONTHS_SHORT_OF, todayIn, zonedParts } from './time'
import { fmtDuration } from './uptime'

/** C-12 incident history: "Wed, Oct 7 · 4:23 PM – 4:32 PM · down 9 min"; "ongoing" while open. */
export function incidentMeta(
  incident: { startedAt: string; endedAt: string | null; downMinutes: number },
  timeZone: string,
): string {
  const end = incident.endedAt === null ? 'ongoing' : fmtTime(incident.endedAt, timeZone)
  return `${fmtDayOf(incident.startedAt, timeZone)} · ${fmtTime(incident.startedAt, timeZone)} – ${end} · down ${fmtDuration(incident.downMinutes)}`
}

/** The tooltip's incident times: "4:23 PM – 4:32 PM" ("ongoing" while open). */
export function incidentSpan(incident: { startedAt: string; endedAt: string | null }, timeZone: string): string {
  return `${fmtTime(incident.startedAt, timeZone)} – ${incident.endedAt === null ? 'ongoing' : fmtTime(incident.endedAt, timeZone)}`
}

/** The tooltip's date: "Wed, Oct 7, 2026" for a clinic-local `YYYY-MM-DD`. */
export function tooltipDate(date: string): string {
  const d = new Date(`${date}T12:00:00Z`)
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d)
}

/** "Down (since 4:23 PM)" today; "Down (since Oct 7, 4:23 PM)" before today. */
export function sinceText(since: string, timeZone: string, now: number): string {
  const day = zonedParts(since, timeZone).date
  const time = fmtTime(since, timeZone)
  if (day === todayIn(timeZone, now)) return time
  return `${MONTHS_SHORT_OF(day)} ${String(Number(day.slice(8, 10)))}, ${time}`
}

/** Minutes since an instant (an open incident's "down {d}"), at least 1. */
export const minutesSince = (iso: string, now: number): number =>
  Math.max(1, Math.ceil((now - Date.parse(iso)) / 60_000))
